import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import MapView from "../components/MapView";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import MultiImageUpload from "../components/MultiImageUpload";
import UKLocationAutocomplete from "../components/UKLocationAutocomplete";
import { api, getUser } from "../lib/api";
import { capture } from "../lib/analytics";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Trash2, Truck, Home as HomeIcon, Leaf, KeyRound,
  Move as MoveIcon, Hammer, Building2, AlertTriangle, CheckCircle2, Package, Car,
} from "lucide-react";

const TYPES = ["Green / Garden", "Recycling", "Bulky furniture", "Food / Organic", "Construction", "Electronics", "Hazardous", "Mixed"];

// Task 3 — visual load-size selector. Keys map to backend `load_size`
// values (see `WastePost.load_size` in deps.py). Exported label map is
// reused by the marketplace card + job-detail badge.
export const LOAD_SIZES = [
  { id: "single_item",  label: "Single item",           icon: Package, hint: "One sofa, mattress, appliance" },
  { id: "car_boot",     label: "Car boot",              icon: Car,     hint: "A few bags — fits in a boot" },
  { id: "small_van",    label: "Small van load",        icon: Truck,   hint: "Fills a Transit Connect or Berlingo" },
  { id: "large_van",    label: "Large van load",        icon: Truck,   hint: "LWB Transit / Sprinter equivalent" },
  { id: "luton",        label: "Luton / large clearance", icon: Truck, hint: "Luton box van or larger" },
];
export const LOAD_SIZE_LABEL = Object.fromEntries(LOAD_SIZES.map((l) => [l.id, l.label]));

// Clear sub-categories — order shown on Step 2.
const CLEAR_SUBTYPES = [
  { id: "house", label: "House clearance", icon: HomeIcon, tint: "#1B3624", body: "Whole home, end-of-life clear-out, bereavement." },
  { id: "garden", label: "Garden clearance", icon: Leaf, tint: "#4A6B56", body: "Overgrown garden, hedge, shed, decking." },
  { id: "end_of_tenancy", label: "End of tenancy", icon: KeyRound, tint: "#D67B54", body: "Rental hand-back — deposit-safe clear." },
  { id: "moving_house", label: "Moving house", icon: MoveIcon, tint: "#4A6B56", body: "Left-behind items after a move-out." },
  { id: "diy_renovation", label: "DIY & renovation", icon: Hammer, tint: "#D67B54", body: "Rubble, plasterboard, timber, offcuts." },
  { id: "commercial", label: "Commercial", icon: Building2, tint: "#1B3624", body: "Office refit, retail, WEEE, trade waste." },
];

// Quick lookup used by the AI suggestion chip.
const SUBTYPE_LABELS = Object.fromEntries(CLEAR_SUBTYPES.map((s) => [s.id, s.label]));

// Chip lists used inside Step 3 conditional sections.
const HOUSE_HAZARDS = ["Fridge / freezer", "TV / monitor", "Paint tins", "Batteries", "Asbestos suspected"];
const DIY_MATERIALS = ["Plasterboard", "Rubble / bricks", "Metals", "Wood / timber", "Insulation"];
const COMMERCIAL_TYPES = ["Office", "Retail", "Hospitality", "Warehouse", "Other"];
const ROOM_CHIPS = ["Lounge", "Kitchen", "Bedrooms", "Bathrooms", "Garage", "Loft", "Garden"];

export default function PostWaste() {
  const navigate = useNavigate();
  const user = getUser();

  // Wizard step. 1 = job type · 2 = clear subtype (skipped for Remove) · 3 = details.
  const [step, setStep] = useState(1);

  const [jobType, setJobType] = useState(null); // "remove" | "clear"
  const [subtype, setSubtype] = useState(null); // one of CLEAR_SUBTYPES[i].id

  const [form, setForm] = useState({
    title: "",
    waste_type: "Mixed",
    volume: "",
    load_size: null,
    is_residential: true,
    easy_access: true,
    description: "",
    address: "",
    preferred_date: "",
    access_details: "",
    image_urls: [],
    location: null,
  });

  // Sub-type specific extras (flat here; folded into `clearance_details` on submit).
  const [details, setDetails] = useState({});

  // Phase 4 — Haiku-powered "Looks like a garden clearance?" chip on
  // Clear jobs. Runs after the user types a description; purely optional.
  const [suggestedSubtype, setSuggestedSubtype] = useState(null);
  const [suggestDismissed, setSuggestDismissed] = useState(false);
  useEffect(() => {
    if (jobType !== "clear") { setSuggestedSubtype(null); return; }
    const desc = form.description.trim();
    if (desc.length < 20) { setSuggestedSubtype(null); return; }
    const controller = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(
          "/clear-subtype/suggest",
          { description: desc, title: form.title || undefined },
          { signal: controller.signal }
        );
        const pick = data?.subtype;
        if (pick && pick !== subtype) setSuggestedSubtype(pick);
      } catch { /* soft-fail — never block the form */ }
    }, 900);
    return () => { clearTimeout(t); controller.abort(); };
  }, [form.description, form.title, jobType, subtype]);

  const loc = form.location;

  // Asbestos on DIY jobs is a hard block — surfaces a friendly message so
  // users know why the submit is disabled. Aligned with Policies §6.
  const asbestosBlocked = jobType === "clear" && subtype === "diy_renovation" && !!details.asbestos;

  const canSubmit = useMemo(() => {
    if (!jobType) return false;
    if (jobType === "clear" && !subtype) return false;
    if (!loc) return false;
    if (!form.image_urls || form.image_urls.length === 0) return false;
    if (!form.title || !form.volume || !form.description) return false;
    if (asbestosBlocked) return false;
    return true;
  }, [jobType, subtype, loc, form, asbestosBlocked]);

  if (!user) {
    navigate("/login");
    return null;
  }
  if (user.role === "carrier") {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-5 py-20 text-center">
          <h2 className="text-2xl font-medium" style={{ fontFamily: "Outfit" }}>Carriers can't post jobs</h2>
          <p className="mt-2 text-[#6B6965]">Switch to a homeowner or business account to post jobs.</p>
        </div>
      </Layout>
    );
  }

  const goStep2 = () => {
    if (!jobType) {
      toast.error("Pick Remove or Clear to continue");
      return;
    }
    // Remove skips Step 2 entirely.
    setStep(jobType === "remove" ? 3 : 2);
  };
  const goStep3 = () => {
    if (jobType === "clear" && !subtype) {
      toast.error("Pick a sub-category to continue");
      return;
    }
    setStep(3);
  };
  const back = () => {
    if (step === 3) setStep(jobType === "remove" ? 1 : 2);
    else if (step === 2) setStep(1);
  };

  const toggleChip = (key, value) => {
    setDetails((d) => {
      const current = Array.isArray(d[key]) ? d[key] : [];
      return {
        ...d,
        [key]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
      };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      const payload = {
        title: form.title,
        waste_type: form.waste_type,
        volume: form.volume,
        description: form.description,
        address: form.address || loc.address || loc.label,
        lat: loc.lat,
        lng: loc.lng,
        image_urls: form.image_urls,
        image_url: form.image_urls[0],
        preferred_date: form.preferred_date || null,
        access_details: form.access_details.trim() || null,
        job_type: jobType,
        job_subtype: jobType === "clear" ? subtype : null,
        load_size: form.load_size || null,
        is_residential: form.is_residential,
        easy_access: form.easy_access,
        clearance_details: jobType === "clear" ? details : {},
      };
      const { data } = await api.post(`/waste-posts`, payload, { params: { user_id: user.id } });
      capture("post_waste_clicked", {
        job_type: jobType,
        job_subtype: jobType === "clear" ? subtype : null,
        waste_type: form.waste_type,
        photo_count: form.image_urls.length,
      });
      toast.success(
        jobType === "clear"
          ? "Clear job posted — licensed carriers can now bid."
          : "Job posted — carriers can now bid!"
      );
      navigate(`/waste/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post");
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <h1
          className="text-3xl sm:text-4xl tracking-tighter font-medium"
          style={{ fontFamily: "Outfit" }}
          data-testid="post-waste-title"
        >
          {jobType === "clear" ? "Clear a whole space" : jobType === "remove" ? "Get it removed" : "Post a job"}
        </h1>
        <p className="mt-2 text-sm text-[#6B6965] max-w-xl">
          Answer a few short questions — licensed UK carriers will bid within minutes and you pick the best price.
        </p>

        <Stepper step={step} jobType={jobType} />

        {/* STEP 1 — Remove or Clear */}
        {step === 1 && (
          <div data-testid="pw-step-1" className="mt-8 grid sm:grid-cols-2 gap-4">
            <TypeTile
              testId="pw-type-remove"
              active={jobType === "remove"}
              onClick={() => setJobType("remove")}
              icon={Trash2}
              tint="#D67B54"
              title="Remove"
              body="One-off item or a few things — sofa, mattress, fridge, garden bags."
            />
            <TypeTile
              testId="pw-type-clear"
              active={jobType === "clear"}
              onClick={() => setJobType("clear")}
              icon={Truck}
              tint="#1B3624"
              title="Clear"
              body="A whole room, garden or tenancy — one pro job, one price."
            />
            <div className="sm:col-span-2 flex justify-end pt-2">
              <Button
                data-testid="pw-next-1"
                onClick={goStep2}
                disabled={!jobType}
                className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white px-7 py-5 disabled:opacity-50"
              >
                Continue <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2 — Clear sub-category */}
        {step === 2 && (
          <div data-testid="pw-step-2" className="mt-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {CLEAR_SUBTYPES.map((s) => (
                <SubtypeTile
                  key={s.id}
                  testId={`pw-sub-${s.id}`}
                  active={subtype === s.id}
                  onClick={() => setSubtype(s.id)}
                  icon={s.icon}
                  tint={s.tint}
                  title={s.label}
                  body={s.body}
                />
              ))}
            </div>
            <div className="mt-6 flex items-center justify-between">
              <Button
                data-testid="pw-back-2"
                onClick={back}
                variant="ghost"
                className="rounded-full text-[#1B3624] hover:bg-[#EBE8E0]"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button
                data-testid="pw-next-2"
                onClick={goStep3}
                disabled={!subtype}
                className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white px-7 py-5 disabled:opacity-50"
              >
                Continue <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 — Details form */}
        {step === 3 && (
          <div data-testid="pw-step-3" className="mt-8 grid lg:grid-cols-2 gap-6">
            <Card className="p-7 rounded-2xl border-[#E2DFD8] bg-white">
              <form onSubmit={submit} className="space-y-4">
                <Field label="Title">
                  <Input
                    data-testid="pw-title"
                    value={form.title}
                    required
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder={jobType === "clear" ? "e.g. End-of-tenancy clearance — 2 bed flat" : "e.g. Old sofa & armchair"}
                    className="rounded-xl"
                  />
                </Field>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Waste type">
                    <Select value={form.waste_type} onValueChange={(v) => setForm({ ...form, waste_type: v })}>
                      <SelectTrigger data-testid="pw-type" className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Volume">
                    <Input
                      data-testid="pw-volume"
                      required
                      value={form.volume}
                      onChange={(e) => setForm({ ...form, volume: e.target.value })}
                      placeholder="4 bags / 1 skip"
                      className="rounded-xl"
                    />
                  </Field>
                </div>
                {/* Task 3 — visual load-size selector */}
                <div data-testid="pw-load-size" className="space-y-2">
                  <Label className="text-sm">Load size <span className="text-[#6B6965] font-normal">(helps carriers match the right vehicle)</span></Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {LOAD_SIZES.map((s) => {
                      const active = form.load_size === s.id;
                      const IconEl = s.icon;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          data-testid={`pw-load-${s.id}`}
                          onClick={() => setForm((f) => ({ ...f, load_size: active ? null : s.id }))}
                          aria-pressed={active}
                          className={`text-left rounded-2xl border-2 p-3 min-h-[92px] flex flex-col transition-all ${
                            active
                              ? "border-[#1B3624] bg-[#1B3624]/5"
                              : "border-[#E2DFD8] bg-white hover:border-[#4A6B56]"
                          }`}
                        >
                          <div
                            className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${
                              active ? "bg-[#1B3624] text-white" : "bg-[#EBE8E0] text-[#1B3624]"
                            }`}
                          >
                            <IconEl className={`${s.id === "single_item" ? "w-4 h-4" : s.id === "car_boot" ? "w-4 h-4" : s.id === "small_van" ? "w-4 h-4" : s.id === "large_van" ? "w-5 h-5" : "w-6 h-6"}`} />
                          </div>
                          <div className="text-sm font-medium leading-tight text-[#1B3624]" style={{ fontFamily: "Outfit" }}>{s.label}</div>
                          <div className="text-[11px] text-[#6B6965] leading-snug mt-0.5">{s.hint}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {/* Task 4 — residential + access toggles */}
                <div className="grid sm:grid-cols-2 gap-3">
                  <label
                    data-testid="pw-residential-row"
                    htmlFor="pw-residential"
                    className="flex items-start gap-3 rounded-2xl border border-[#E2DFD8] bg-white p-4 cursor-pointer hover:border-[#4A6B56] transition-colors"
                  >
                    <div className="flex-1">
                      <div className="text-sm font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>
                        Is this a residential address?
                      </div>
                      <div className="text-[11px] text-[#6B6965] mt-0.5">
                        {form.is_residential ? "Yes — home / flat" : "No — commercial / business"}
                      </div>
                    </div>
                    <Switch
                      id="pw-residential"
                      data-testid="pw-is-residential"
                      checked={form.is_residential}
                      onCheckedChange={(v) => setForm((f) => ({ ...f, is_residential: v }))}
                    />
                  </label>
                  <label
                    data-testid="pw-access-row"
                    htmlFor="pw-easy-access"
                    className="flex items-start gap-3 rounded-2xl border border-[#E2DFD8] bg-white p-4 cursor-pointer hover:border-[#4A6B56] transition-colors"
                  >
                    <div className="flex-1">
                      <div className="text-sm font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>
                        Is there easy vehicle access?
                      </div>
                      <div className="text-[11px] text-[#6B6965] mt-0.5">
                        {form.easy_access ? "Yes — driveway or kerbside" : "No — narrow lane, gates, stairs"}
                      </div>
                    </div>
                    <Switch
                      id="pw-easy-access"
                      data-testid="pw-easy-access"
                      checked={form.easy_access}
                      onCheckedChange={(v) => setForm((f) => ({ ...f, easy_access: v }))}
                    />
                  </label>
                </div>
                <Field label="Description">
                  <Textarea
                    data-testid="pw-desc"
                    required
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="What's the waste, access notes…"
                    className="rounded-xl min-h-[100px]"
                  />
                </Field>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="UK location (city, town or postcode)">
                    <UKLocationAutocomplete
                      value={form.location}
                      onChange={(v) => setForm({ ...form, location: v })}
                      placeholder="e.g. M1 1AE, Manchester or Leeds LS1"
                      testId="pw-area"
                    />
                  </Field>
                  <Field label="Street address (optional)">
                    <Input
                      data-testid="pw-address"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      placeholder="12 Acacia Rd"
                      className="rounded-xl"
                    />
                  </Field>
                </div>
                <Field label="Preferred date (optional)">
                  <Input
                    data-testid="pw-date"
                    type="date"
                    value={form.preferred_date}
                    onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                    className="rounded-xl"
                  />
                </Field>

                {/* Conditional per-subtype extras */}
                {jobType === "clear" && subtype && (
                  <ClearExtras
                    subtype={subtype}
                    details={details}
                    setDetails={setDetails}
                    toggleChip={toggleChip}
                    asbestosBlocked={asbestosBlocked}
                  />
                )}

                {/* Phase 4 — soft AI suggestion. Only if the model picked a
                    different subtype than the user did (or one that fits an
                    unset field). */}
                {jobType === "clear" &&
                  suggestedSubtype &&
                  suggestedSubtype !== subtype &&
                  !suggestDismissed && (
                    <div
                      data-testid="pw-subtype-suggest"
                      className="flex items-center justify-between gap-2 rounded-2xl border border-[#4A6B56]/30 bg-[#4A6B56]/5 p-3 text-sm text-[#1B3624]"
                    >
                      <div>
                        Looks like a{" "}
                        <span className="font-medium">
                          {SUBTYPE_LABELS[suggestedSubtype] || suggestedSubtype}
                        </span>?
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          type="button"
                          data-testid="pw-subtype-suggest-accept"
                          size="sm"
                          onClick={() => {
                            setSubtype(suggestedSubtype);
                            setSuggestDismissed(true);
                          }}
                          className="rounded-full bg-[#4A6B56] hover:bg-[#3d5947] text-white h-8"
                        >
                          Use it
                        </Button>
                        <Button
                          type="button"
                          data-testid="pw-subtype-suggest-dismiss"
                          size="sm"
                          variant="ghost"
                          onClick={() => setSuggestDismissed(true)}
                          className="rounded-full text-[#6B6965] h-8"
                        >
                          Dismiss
                        </Button>
                      </div>
                    </div>
                  )}

                <div className="rounded-2xl border border-[#D67B54]/25 bg-[#D67B54]/5 p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#D67B54]" />
                    <div className="text-[11px] uppercase tracking-widest text-[#D67B54] font-medium">Collection &amp; Access Details</div>
                  </div>
                  <p className="text-xs text-[#6B6965]">
                    Help carriers quote accurately — mention stairs, parking, gates, timing windows or anything that affects how they'll collect.
                  </p>
                  <Textarea
                    data-testid="pw-access"
                    value={form.access_details}
                    onChange={(e) => setForm({ ...form, access_details: e.target.value })}
                    placeholder="e.g., Sofa on 4th floor, no lift, requires 2 people to carry"
                    className="rounded-xl min-h-[90px] bg-white border-[#E2DFD8] focus-visible:ring-[#D67B54]"
                  />
                </div>

                <Field label="Photos (at least one required)">
                  <MultiImageUpload
                    value={form.image_urls}
                    onChange={(urls) => setForm({ ...form, image_urls: urls })}
                    max={6}
                    testId="pw-image"
                  />
                  <p className="mt-1 text-xs text-[#6B6965]">
                    Add up to 6 clear photos — different angles help carriers quote accurately. The first photo is the main one shown on your listing.
                  </p>
                </Field>

                <div className="flex items-center justify-between pt-2">
                  <Button
                    data-testid="pw-back-3"
                    type="button"
                    onClick={back}
                    variant="ghost"
                    className="rounded-full text-[#1B3624] hover:bg-[#EBE8E0]"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" /> Back
                  </Button>
                  <Button
                    data-testid="pw-submit"
                    type="submit"
                    disabled={!canSubmit}
                    className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white px-7 py-6 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Post &amp; receive bids <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </form>
            </Card>

            <div>
              <div className="text-sm text-[#6B6965] mb-2">Pin location preview</div>
              <MapView
                markers={loc ? [{ id: "preview", lat: loc.lat, lng: loc.lng, title: form.title || "Your job", subtitle: loc.label }] : []}
                center={loc ? [loc.lat, loc.lng] : [54.5, -2.5]}
                zoom={loc ? 14 : 5}
                height="h-[420px]"
                testId="pw-map"
              />
              {!loc && (
                <p className="mt-2 text-xs text-[#6B6965]">Pick your UK location on the left to drop a pin.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function Stepper({ step, jobType }) {
  const totalSteps = jobType === "remove" ? 2 : 3;
  const shownStep = jobType === "remove" && step === 3 ? 2 : step;
  return (
    <div data-testid="pw-stepper" className="mt-6 flex items-center gap-2 text-xs uppercase tracking-widest text-[#9B9893]">
      <span data-testid="pw-step-indicator" className="font-medium text-[#1B3624]">Step {shownStep}</span>
      <span>of {totalSteps}</span>
    </div>
  );
}

function TypeTile({ testId, active, onClick, icon: Icon, tint, title, body }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`text-left rounded-3xl p-6 border-2 transition ${
        active
          ? "border-[#1B3624] bg-[#F6F4F0] shadow-sm"
          : "border-[#E2DFD8] bg-white hover:border-[#1B3624]/40 hover:bg-[#FBFAF6]"
      }`}
    >
      <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: tint }}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <h3 className="mt-4 text-2xl font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>{title}</h3>
      <p className="mt-2 text-sm text-[#6B6965] leading-relaxed">{body}</p>
    </button>
  );
}

function SubtypeTile({ testId, active, onClick, icon: Icon, tint, title, body }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`text-left rounded-2xl p-5 border transition ${
        active
          ? "border-[#1B3624] bg-[#F6F4F0]"
          : "border-[#E2DFD8] bg-white hover:border-[#1B3624]/40"
      }`}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: tint }}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="mt-3 font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>{title}</div>
      <div className="mt-1 text-xs text-[#6B6965] leading-relaxed">{body}</div>
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Chip({ testId, active, onClick, children }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`rounded-full border text-xs px-3 py-1.5 transition ${
        active
          ? "border-[#1B3624] bg-[#1B3624] text-white"
          : "border-[#E2DFD8] hover:border-[#1B3624] hover:bg-[#F6F4F0] text-[#1A1A1A]"
      }`}
    >
      {children}
    </button>
  );
}

function ClearExtras({ subtype, details, setDetails, toggleChip, asbestosBlocked }) {
  const chipList = (label, key, values, testPrefix) => (
    <Field label={label}>
      <div className="flex flex-wrap gap-2">
        {values.map((v) => (
          <Chip
            key={v}
            testId={`${testPrefix}-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            active={Array.isArray(details[key]) && details[key].includes(v)}
            onClick={() => toggleChip(key, v)}
          >
            {v}
          </Chip>
        ))}
      </div>
    </Field>
  );

  return (
    <div data-testid={`pw-extras-${subtype}`} className="rounded-2xl border border-[#1B3624]/15 bg-[#F6F4F0] p-4 space-y-4">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4 text-[#4A6B56]" />
        <div className="text-[11px] uppercase tracking-widest text-[#1B3624] font-medium">Extra details for this sub-category</div>
      </div>

      {subtype === "house" && (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Number of rooms">
              <Input
                data-testid="pw-rooms"
                type="number"
                min={1}
                value={details.rooms || ""}
                onChange={(e) => setDetails({ ...details, rooms: e.target.value })}
                placeholder="3"
                className="rounded-xl bg-white"
              />
            </Field>
            <Field label="Floor(s)">
              <Input
                data-testid="pw-floors"
                value={details.floors || ""}
                onChange={(e) => setDetails({ ...details, floors: e.target.value })}
                placeholder="Ground + 1st"
                className="rounded-xl bg-white"
              />
            </Field>
          </div>
          {chipList("Hazardous items on site", "hazards", HOUSE_HAZARDS, "pw-hazard")}
        </>
      )}

      {subtype === "garden" && (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Approx m² or bag count">
              <Input
                data-testid="pw-garden-size"
                value={details.size || ""}
                onChange={(e) => setDetails({ ...details, size: e.target.value })}
                placeholder="30 m² or 12 bags"
                className="rounded-xl bg-white"
              />
            </Field>
          </div>
          <label className="flex items-center gap-3 text-sm text-[#1B3624]">
            <Checkbox
              data-testid="pw-garden-soil"
              checked={!!details.contains_soil}
              onCheckedChange={(v) => setDetails({ ...details, contains_soil: Boolean(v) })}
            />
            Contains soil
          </label>
          <label className="flex items-center gap-3 text-sm text-[#1B3624]">
            <Checkbox
              data-testid="pw-garden-treated"
              checked={!!details.treated_timber}
              onCheckedChange={(v) => setDetails({ ...details, treated_timber: Boolean(v) })}
            />
            Contains treated / painted timber
          </label>
        </>
      )}

      {subtype === "end_of_tenancy" && (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Tenancy end date">
              <Input
                data-testid="pw-eot-end"
                type="date"
                value={details.tenancy_end || ""}
                onChange={(e) => setDetails({ ...details, tenancy_end: e.target.value })}
                className="rounded-xl bg-white"
              />
            </Field>
            <Field label="Key handover date">
              <Input
                data-testid="pw-eot-handover"
                type="date"
                value={details.key_handover || ""}
                onChange={(e) => setDetails({ ...details, key_handover: e.target.value })}
                className="rounded-xl bg-white"
              />
            </Field>
          </div>
          <Field label="Landlord / letting agent contact (optional)">
            <Input
              data-testid="pw-eot-landlord"
              value={details.landlord_contact || ""}
              onChange={(e) => setDetails({ ...details, landlord_contact: e.target.value })}
              placeholder="Name + phone or email"
              className="rounded-xl bg-white"
            />
          </Field>
        </>
      )}

      {subtype === "moving_house" && (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Move date">
              <Input
                data-testid="pw-move-date"
                type="date"
                value={details.move_date || ""}
                onChange={(e) => setDetails({ ...details, move_date: e.target.value })}
                className="rounded-xl bg-white"
              />
            </Field>
            <Field label="Destination postcode (optional)">
              <Input
                data-testid="pw-move-dest"
                value={details.destination_postcode || ""}
                onChange={(e) => setDetails({ ...details, destination_postcode: e.target.value })}
                placeholder="e.g. SW1A 1AA"
                className="rounded-xl bg-white"
              />
            </Field>
          </div>
          {chipList("Rooms to clear", "rooms", ROOM_CHIPS, "pw-move-room")}
        </>
      )}

      {subtype === "diy_renovation" && (
        <>
          {chipList("Materials on site", "materials", DIY_MATERIALS, "pw-diy-mat")}
          <label className="flex items-center gap-3 text-sm text-[#1B3624]">
            <Checkbox
              data-testid="pw-diy-asbestos"
              checked={!!details.asbestos}
              onCheckedChange={(v) => setDetails({ ...details, asbestos: Boolean(v) })}
            />
            Asbestos suspected on site
          </label>
          {asbestosBlocked && (
            <div
              data-testid="pw-asbestos-block"
              className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50/70 p-3 text-xs text-red-800"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                Asbestos requires a specialist hazardous-waste carrier and cannot be posted through Binshare's standard flow.
                Please contact a licensed asbestos removal service directly — see our Policies page (Section 6) for guidance.
              </div>
            </div>
          )}
        </>
      )}

      {subtype === "commercial" && (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Business type">
              <Select
                value={details.business_type || ""}
                onValueChange={(v) => setDetails({ ...details, business_type: v })}
              >
                <SelectTrigger data-testid="pw-com-business" className="rounded-xl bg-white">
                  <SelectValue placeholder="Pick one" />
                </SelectTrigger>
                <SelectContent>
                  {COMMERCIAL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Weekly volume">
              <Input
                data-testid="pw-com-volume"
                value={details.weekly_volume || ""}
                onChange={(e) => setDetails({ ...details, weekly_volume: e.target.value })}
                placeholder="e.g. 240 L / 3 bags"
                className="rounded-xl bg-white"
              />
            </Field>
          </div>
          <Field label="Site access hours">
            <Input
              data-testid="pw-com-hours"
              value={details.access_hours || ""}
              onChange={(e) => setDetails({ ...details, access_hours: e.target.value })}
              placeholder="e.g. Mon–Fri 08:00–18:00"
              className="rounded-xl bg-white"
            />
          </Field>
          <label className="flex items-center gap-3 text-sm text-[#1B3624]">
            <Checkbox
              data-testid="pw-com-weee"
              checked={!!details.weee}
              onCheckedChange={(v) => setDetails({ ...details, weee: Boolean(v) })}
            />
            Contains WEEE (electricals)
          </label>
        </>
      )}
    </div>
  );
}
